import { useContext, useEffect } from "react";
import { useFetcher, useNavigate } from "@remix-run/react";
import pluralize from "pluralize";

import OrderContext from "~/contexts/OrderContext";
import { OrderSource } from "~/entities/orderEntities";
import type { OrgSites, Site } from "~/entities/siteEntities";
import { settingsFor } from "~/helpers/leadTime";
import {
  orderTypesForSource,
  siteListDoesCatering,
} from "~/helpers/orderHelper";
import { urlGen } from "~/helpers/routesHelpers";
import { findOrderTypeForFulfilment } from "~/helpers/storeFinderFulfilment";

export type CateringEntry = {
  title: string;
  detail: string;
  go: VoidFunction;
};

// "5 days" / "4 hours": how soon a store's catering can be booked, for "Order ahead for events, from 2 days out"
const soonestFor = (site: Site): string | null => {
  const pickup =
    findOrderTypeForFulfilment(
      site.orderTypes,
      "PICKUP",
      OrderSource.CATERING,
    ) ??
    findOrderTypeForFulfilment(
      site.orderTypes,
      "DELIVERY",
      OrderSource.CATERING,
    );
  const settings = pickup
    ? settingsFor(site.serviceSettings, pickup.code)
    : undefined;
  if (!settings || settings.allowAsapOrders) {
    return null;
  }
  const days = settings.schedulingRules?.minDaysFromNow ?? 0;
  if (days >= 1) {
    return pluralize("day", days, true);
  }
  const minutes = settings.schedulingRules?.minMinutesFromNow ?? 0;
  return minutes >= 60
    ? pluralize("hour", Math.ceil(minutes / 60), true)
    : null;
};

// The nav's way into catering, or back to everyday ordering from it (C1). Null when the org has no catering stores.
// The org's store list is only fetched once the menu is open, and only when this store doesn't do catering itself.
export const useCateringEntry = (
  site: Site | undefined,
  open: boolean,
  onDone: VoidFunction,
): CateringEntry | null => {
  const { orderSource, otherExperienceCart, openStoreFinder } =
    useContext(OrderContext);
  const navigate = useNavigate();
  const orgSites = useFetcher<OrgSites>();

  const inCatering = orderSource === OrderSource.CATERING;
  const siteHasCatering =
    !!site &&
    orderTypesForSource(site.orderTypes, OrderSource.CATERING).length > 0;

  useEffect(() => {
    if (
      open &&
      !inCatering &&
      !siteHasCatering &&
      orgSites.state === "idle" &&
      !orgSites.data
    ) {
      orgSites.load("/api/orgSites");
    }
  }, [open, inCatering, siteHasCatering, orgSites]);

  if (inCatering) {
    const everyday =
      otherExperienceCart?.orderSource === OrderSource.ONLINE
        ? otherExperienceCart
        : null;
    const count = everyday?.orderItemCount ?? 0;
    return {
      title: "Order food",
      detail: `Back to everyday ordering${
        count ? ` · ${pluralize("item", count, true)} in your cart` : ""
      }`,
      go: () => {
        navigate(
          urlGen("Order", {
            orderSource: OrderSource.ONLINE,
            siteId: everyday?.siteId ?? site?.id,
          }),
        );
        onDone();
      },
    };
  }

  const orgHasCatering =
    siteHasCatering || !!orgSites.data?.sites?.some(siteListDoesCatering);
  if (!orgHasCatering) {
    return null;
  }
  const soonest = siteHasCatering && site ? soonestFor(site) : null;
  return {
    title: "Catering",
    detail: `Order ahead for events${soonest ? `, from ${soonest} out` : ""}`,
    go: () => {
      // This store's catering if it does it, else the catering picker
      if (siteHasCatering && site) {
        navigate(
          urlGen("Order", {
            orderSource: OrderSource.CATERING,
            siteId: site.id,
          }),
        );
      } else {
        openStoreFinder(undefined, OrderSource.CATERING);
      }
      onDone();
    },
  };
};
